import type { JsonObject } from "@/lib/json";
import { queryOptions, type QueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { request, json } from "@/lib/queries/whiteboards";
import {
  type WhiteboardPermission,
  whiteboardDetailSchema,
  whiteboardSummarySchema,
  whiteboardShareEntrySchema,
} from "@/lib/whiteboard/schema";
import {
  adminWhiteboardListSchema,
  type AdminWhiteboardSearch,
  type AdminWhiteboardUpdate,
} from "@/lib/whiteboard/admin-schema";

export const adminWhiteboardKey = ["admin", "whiteboards"] as const;
const path = (id: string, rest = "") => `/api/admin/whiteboards/${encodeURIComponent(id)}${rest}`;

export const adminWhiteboardsQueryOptions = (search: AdminWhiteboardSearch = {}) =>
  queryOptions({
    queryKey: [...adminWhiteboardKey, "list", search],
    queryFn: async () => {
      const params = new URLSearchParams();

      if (search.q) params.set("q", search.q);

      if (search.projectId) params.set("projectId", search.projectId);

      if (search.external) params.set("external", search.external);
      params.set("page", String(search.page ?? 1));

      return adminWhiteboardListSchema.parse(
        await (await request(`/api/admin/whiteboards?${params}`)).json(),
      );
    },
    retry: false,
  });

export const adminWhiteboardQueryOptions = (id: string) =>
  queryOptions({
    queryKey: [...adminWhiteboardKey, "detail", id],
    queryFn: async () => whiteboardDetailSchema.parse(await (await request(path(id))).json()),
    retry: false,
  });

export const adminWhiteboardSharesQueryOptions = (id: string) =>
  queryOptions({
    queryKey: [...adminWhiteboardKey, "shares", id],
    queryFn: async () =>
      z.array(whiteboardShareEntrySchema).parse(await (await request(path(id, "/shares"))).json()),
    retry: false,
  });

export const adminWhiteboardProjectsQueryOptions = queryOptions({
  queryKey: [...adminWhiteboardKey, "projects"],
  queryFn: async () =>
    z
      .array(z.object({ id: z.string(), name: z.string(), projectNumber: z.string() }))
      .parse(await (await request("/api/admin/whiteboards/projects")).json()),
  retry: false,
});

export const adminWhiteboardPreviewUrl = (id: string, previewAt: string) =>
  `${path(id, "/preview")}?v=${encodeURIComponent(previewAt)}`;

export async function saveAdminWhiteboard(id: string, change: AdminWhiteboardUpdate) {
  return whiteboardSummarySchema.parse(
    await (await request(path(id), json("PATCH", change))).json(),
  );
}

export async function removeAdminWhiteboard(id: string, revision: number) {
  await request(path(id), json("DELETE", { revision }));
}

export async function changeAdminWhiteboardShare(
  id: string,
  method: "POST" | "PATCH" | "DELETE",
  userId: string,
  permission?: WhiteboardPermission,
) {
  const body: JsonObject = { userId };

  if (permission) body.permission = permission;

  return z
    .array(whiteboardShareEntrySchema)
    .parse(await (await request(path(id, "/shares"), json(method, body))).json());
}

export function invalidateAdminWhiteboards(client: QueryClient) {
  // Personal lists and project resources may already be cached in this session.
  void client.invalidateQueries({ queryKey: adminWhiteboardKey });
  void client.invalidateQueries({ queryKey: ["whiteboards"] });
}
